'use strict';

import React  from 'react';
import PropTypes from 'prop-types';
import bb from 'billboard.js';
const d3 = require("d3");
const _  = {
    forEach: require('lodash/forEach'),
}

import Tooltip from '../common/Tooltip';
import {H, SmartSection } from "../common/SmartHeadings";

const Globals = require('../../../../app/globals');

const DEFAULT_HEIGHT = 230;
const DEFAULT_WIDTH = undefined; // responsive width
const DEFAULT_TITLE_FS = 3;

const propTypes = {
    className: PropTypes.string,
    columns: PropTypes.array,
    colors: PropTypes.object,
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    height: PropTypes.number,
    width: PropTypes.number,
    tooltipFormat: PropTypes.oneOf(['', '$', ',', '%', '.0%']),
    tooltipFormatValue: PropTypes.oneOf(['value', 'ratio', 'id', 'index']),
    transitionDuration: PropTypes.number,
    legendShow: PropTypes.bool,
    yInt: PropTypes.bool,
    yMin: PropTypes.number,
    xAxisType: PropTypes.oneOf(['category', 'indexed', 'log', 'timeseries']),
    removeXTick: PropTypes.bool,
    yTickCount: PropTypes.number,
    yTickFormat: PropTypes.oneOf(['', '$', ',', '%', '.0%']),
    yShow: PropTypes.bool,
    yTickValues: PropTypes.array,
    yPaddingTop: PropTypes.number,
    yPaddingBottom: PropTypes.number,
    legendPosition: PropTypes.string,
    displayTitleForNoData: PropTypes.bool,
    tooltip: PropTypes.string,
    tooltipTitle: PropTypes.string,
    regions: PropTypes.arrayOf(PropTypes.shape({
        axis: PropTypes.oneOf(['x', 'y']).isRequired,
        start: PropTypes.number.isRequired,
        end: PropTypes.number.isRequired,
        class: PropTypes.string,
        opacity: PropTypes.number, // undocumented C3 feature (https://github.com/c3js/c3/issues/1191) (was transferred into billboard.js)
        label: PropTypes.string, // does not yet work. wating on PR: https://github.com/c3js/c3/pull/1853
    })),
    padding: PropTypes.shape({
        top: PropTypes.number,
        right: PropTypes.number,
        bottom: PropTypes.number, // waiting on PR to fix legend-less bottom padding: https://github.com/c3js/c3/issues/1093
        left: PropTypes.number,
    }),
    barWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ ratio: PropTypes.number })]),
    dataOrder: PropTypes.oneOf(['asc', 'desc']),
    labeledBy: '', // provide the id of another element to label the chart (overrides ariaLabel)
    ariaLabel: '', // provide a self-creating aria-label elem + association
};

const defaultProps = {
    className: "",
    columns: [],
    colors: {},
    title: "",
    height: DEFAULT_HEIGHT,
    width: DEFAULT_WIDTH,
    tooltipFormat: '',
    tooltipFormatValue: 'value',
    transitionDuration: 0,
    legendShow: false,
    yInt: false,
    yMin: 0,
    xAxisType: "",
    removeXTick: false,
    yTickCount: -1,
    yTickFormat: '',
    yShow: true,
    yTickValues: [],
    yPaddingTop: 0,
    yPaddingBottom: 0,
    legendPosition: "bottom",
    displayTitleForNoData: false,
    tooltip: "",
    tooltipTitle: "",
    regions: [],
    padding: null,
    barWidth: null,
    dataOrder: null, // retains array ordering
    ariaLabel: '',
    labeledBy: '',
};

class BarChart extends React.Component {
    constructor(props) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        const self = this;

        let size = { height: this.props.height };
        if (this.props.width) {
            size.width = this.props.width;
        }

        let y = {
            min: this.props.yMin,
            show: this.props.yShow,
            padding: {
                top: this.props.yPaddingTop,
                bottom: this.props.yPaddingBottom
            }
        };
        if (this.props.yTickCount >= 0) {
            y.tick = y.tick || {};
            y.tick.count = this.props.yTickCount;
        }

        if (this.props.yInt) {
            y.tick = y.tick || {};
            const intsOnly = (d) => {
                return d % 1 === 0 ? d : "";
            }
            
            if (!self.props.yTickFormat) {
                y.tick.format = (d) => { return intsOnly(d); };
            }
            else {
                y.tick.format = (d) => { return d3.format(self.props.yTickFormat)(intsOnly(d)); };
            }
        }
        else if (this.props.yTickFormat) {
            y.tick = y.tick || {};
            y.tick.format = (d) => { return d3.format(self.props.yTickFormat)(d); };
        }

        if (this.props.yTickValues.length > 0) {
            y.tick = y.tick || {};
            y.tick.values = this.props.yTickValues;
            y.max = this.props.yTickValues[this.props.yTickValues.length-1];
        }

        let x = {};
        if (this.props.xAxisType) {
            x.type = this.props.xAxisType;
        }

        if (this.props.removeXTick) {
            x.tick = x.tick || {};
            x.tick.format = (d) => {
                return "";
            }
        }

        let colors = {...this.props.colors};
        _.forEach(this.props.columns,(entry,i)=>{
            let name = entry[0];
            if (typeof name === 'string') {
                if (!colors[name]) {
                    colors[name] = Globals.CHART_COLORS[i];
                } 
            }
        })

        let chartData = {
            bindto: '#'+self.props.id,
            data: {
                columns: self.props.columns,
                colors,
                order: self.props.dataOrder,
                type: 'bar'
            },
            legend: {
                show: self.props.legendShow,
                position: self.props.legendPosition
            },
            axis: {
                y: y,
                x: x
            },
            size: size,
            tooltip: {
                format: {
                    value: function (value, ratio, id, index) {
                        let valToUse;
                        switch (self.props.tooltipFormatValue) {
                            case 'ratio': {
                                valToUse = ratio;
                                break;
                            }
                            case 'id': {
                                valToUse = id;
                                break;
                            }
                            case 'value':
                            default: {
                                valToUse = value;
                                break;
                            }
                        }

                        return d3.format(self.props.tooltipFormat)(valToUse);
                    }
                }
            },
            transition: {
                duration: self.props.transitionDuration
            }           
        };

        if (this.props.regions.length > 0) {
            chartData.regions = this.props.regions;
        }

        if (this.props.legendShow && !!this.props.legendPosition) {
            chartData.legend.position = this.props.legendPosition;
        }

        if (this.props.padding) {
            chartData.padding = this.props.padding;
        }

        if (this.props.barWidth) {
            chartData.bar = chartData.bar || {};
            chartData.bar.width = this.props.barWidth;
        }

        this.chart = bb.generate(chartData);
    }

    UNSAFE_componentWillReceiveProps(newProps) {
        this.chart.load({
            columns: newProps.columns
        });
    }

    componentWillUnmount() {
        delete this.chart;
    }

    render() {
        let tooltipElem = null;
        if (this.props.tooltip || this.props.tooltipTitle) {
            tooltipElem = (
                <Tooltip
                    id={`${this.props.id}-tooltip`}
                    className="margin-left-md"
                    title={this.props.tooltipTitle}
                >
                    {this.props.tooltip}
                </Tooltip>
            );
        }

        const hasTitleOrTooltip = !!this.props.title || !!tooltipElem;
        let srOnlyLabelId;
        let optionalAriaLabeledByAttr = {};
        if (this.props.labeledBy) {
            optionalAriaLabeledByAttr["aria-labelledby"] = this.props.labeledBy;
        }
        else if (this.props.ariaLabel) {
            srOnlyLabelId = this.props.id + '-sr-only-label';
            optionalAriaLabeledByAttr["aria-labelledby"] = srOnlyLabelId;
        }
        
        return (
            <div className={!!this.props.className ? `chart-container ${this.props.className}` : "chart-container"}>
              <SmartSection component={
                (hasTitleOrTooltip && !this.props.displayTitleForNoData && !this.props.columns.length) ?
                    null
                    :
                    <div className="chart-heading-wrapper">
                        <H>{this.props.title}</H>
                        {tooltipElem}
                    </div>
                }>
                {(!!this.props.ariaLabel) &&
                    <div id={srOnlyLabelId} className="sr-only">
                        {this.props.ariaLabel}
                    </div>
                }
                <div className="chart-wrapper">
                    <div id={this.props.id} style={{width: "100%"}} role="img" {...optionalAriaLabeledByAttr} />
                </div>
              </SmartSection>
            </div>
        );
    }
}

BarChart.propTypes = propTypes;
BarChart.defaultProps = defaultProps;

export default BarChart;
